// Grille responsive de widgets. Lit le layout via le hook useDashboardLayout
// et instancie chaque widget enabled dans une cellule de la grille 12-col.
// Mobile : 1 col empilée. Desktop : grille 12-col, taille via col-span.
import { useMemo, useState } from "react";
import { Loader2, Settings2, Save, RotateCcw, Plus } from "lucide-react";
import { Button } from "../ui/button";
import { useDashboardLayout } from "../../hooks/useDashboardLayout";
import { SIZE_TO_COLSPAN } from "../../widgets/registry";
import WidgetCard from "./WidgetCard";
import WidgetLibrarySheet from "./WidgetLibrarySheet";

export default function DashboardGrid({ hub, role = null, userEmail = null, title }) {
  const {
    layout, catalog, loading, saving, error,
    save, reset, toggleWidget, moveWidget, resizeWidget,
  } = useDashboardLayout(hub, { role, userEmail });
  const [editMode, setEditMode] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const sortedWidgets = useMemo(() => {
    if (!layout?.widgets) return [];
    return [...layout.widgets].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [layout]);

  const visibleWidgets = useMemo(
    () => sortedWidgets.filter((w) => w.enabled !== false),
    [sortedWidgets]
  );

  const catalogById = useMemo(() => {
    const m = {};
    for (const w of catalog) m[w.id] = w;
    return m;
  }, [catalog]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="rounded-xl p-6 text-center"
        style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-error-border, #ef4444)" }}
      >
        <p style={{ color: "var(--oh-error-text, #f87171)" }}>Erreur chargement layout : {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid={`dashboard-${hub}`}>
      {/* Header : titre + actions édition */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-medium" style={{ color: "var(--oh-text)" }}>
            {title || `Dashboard ${hub}`}
          </h1>
          <p className="text-xs mt-0.5" style={{ color: "var(--oh-text-muted)" }}>
            {visibleWidgets.length} widget{visibleWidgets.length > 1 ? "s" : ""} actif
            {visibleWidgets.length > 1 ? "s" : ""} sur {catalog.length} disponibles
          </p>
        </div>
        <div className="flex items-center gap-2">
          {editMode && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setLibraryOpen(true)}
                aria-label="Ouvrir la bibliothèque de widgets"
                data-testid={`dashboard-${hub}-library-open`}
              >
                <Plus className="w-4 h-4 mr-1" />
                Bibliothèque
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => reset()}
                disabled={saving}
                aria-label="Réinitialiser le layout au défaut"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Réinitialiser
              </Button>
              <Button
                size="sm"
                onClick={async () => { await save(layout); setEditMode(false); }}
                disabled={saving}
                data-testid={`dashboard-${hub}-save`}
              >
                <Save className="w-4 h-4 mr-1" />
                Enregistrer
              </Button>
            </>
          )}
          {!editMode && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditMode(true)}
              aria-label="Personnaliser le dashboard"
              data-testid={`dashboard-${hub}-edit`}
              className="border-cyan-500/30 text-cyan-300 hover:bg-cyan-500/10"
            >
              <Settings2 className="w-4 h-4 mr-1" />
              Personnaliser
            </Button>
          )}
        </div>
      </div>

      {/* Grille — 12 colonnes desktop, 1 col mobile */}
      {visibleWidgets.length === 0 ? (
        <div
          className="rounded-xl p-10 text-center"
          style={{ background: "var(--oh-bg-elevated)", border: "1px dashed var(--oh-border)" }}
        >
          <p style={{ color: "var(--oh-text-muted)" }}>Aucun widget actif.</p>
          <Button
            size="sm"
            variant="outline"
            onClick={() => { setEditMode(true); setLibraryOpen(true); }}
            className="mt-3"
            aria-label="Ouvrir la bibliothèque de widgets"
          >
            <Plus className="w-4 h-4 mr-1" />
            Ajouter des widgets
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
          {visibleWidgets.map((w, idx) => (
            <div
              key={w.id}
              className={SIZE_TO_COLSPAN[w.size] || SIZE_TO_COLSPAN.md}
            >
              <WidgetCard
                widget={w}
                meta={catalogById[w.id]}
                editMode={editMode}
                canMoveUp={idx > 0}
                canMoveDown={idx < visibleWidgets.length - 1}
                onToggle={toggleWidget}
                onMove={moveWidget}
                onResize={resizeWidget}
              />
            </div>
          ))}
        </div>
      )}

      <WidgetLibrarySheet
        open={libraryOpen}
        onClose={() => setLibraryOpen(false)}
        catalog={catalog}
        layout={layout}
        onToggle={toggleWidget}
        onMove={moveWidget}
      />
    </div>
  );
}
